import {
  Data,
  ErrorLevel,
  RowHook,
} from 'react-spreadsheet-import/types/types';
import { parseUKDate } from '@shared/utils/date-formatters';
import {
  addValidationError,
  ERROR_MESSAGES,
  parseNumberWithCommas,
} from '../utils';
import { GoodsImportRow } from './types';

const NUMBER_REGEX = /^-?(?:\d{1,3}(?:,\d{3})+|\d+)(?:\.\d+)?$/;

const sanitizeMixedRow = (row: GoodsImportRow): GoodsImportRow => ({
  ...row,
  name: row.name && row.name.trim(),
  remoteId: row.remoteId && row.remoteId.trim(),
  supplier: row.supplier && row.supplier.trim(),
  customer: row.customer && row.customer.trim(),
  quantity: row.quantity && row.quantity.trim(),
  unit: row.unit && row.unit.trim(),
  date: row.date && row.date.trim(),
  totalSpend: row.totalSpend && row.totalSpend.trim(),
});

const validateMixedRow = (
  row: GoodsImportRow,
  addError: (
    field: keyof GoodsImportRow,
    error: { level: ErrorLevel; message: string }
  ) => void
): void => {
  const { quantity } = row;

  // A date that doesn't parse must fail the row here: the upload page maps
  // unparseable dates to null, and dateless rows fall into an arbitrary
  // reporting period on the backend.
  if (row.date && !parseUKDate(row.date)) {
    addValidationError(
      addError,
      'date',
      'error',
      ERROR_MESSAGES.INVALID_DATE_FORMAT
    );
  }

  if (!quantity) {
    addValidationError(
      addError,
      'quantity',
      'error',
      ERROR_MESSAGES.INVALID_QUANTITY
    );
    return;
  }

  if (!NUMBER_REGEX.test(quantity)) {
    addValidationError(
      addError,
      'quantity',
      'error',
      ERROR_MESSAGES.INVALID_QUANTITY
    );
    return;
  }

  const quantityValue = parseNumberWithCommas(quantity);
  if (isNaN(quantityValue) || quantityValue <= 0) {
    addValidationError(
      addError,
      'quantity',
      'error',
      ERROR_MESSAGES.NEGATIVE_QUANTITY
    );
  }
};

export const useMixedRowValidator = (): RowHook<keyof GoodsImportRow> => {
  return (row, addError) => {
    const sanitizedRow = sanitizeMixedRow(row as GoodsImportRow);
    validateMixedRow(sanitizedRow, addError);
    return sanitizedRow as Data<keyof GoodsImportRow>;
  };
};
